import router from '@/router'
import { useUserStore } from '@/store/user'
import { useSocietyStore } from '@/store/society'
import { useHomeStore } from '@/store/home'
import { useInboxStore } from '@/store/inbox'
import { useBackgroundTasksStore } from '@/store/backgroundTasks'
import { getAuth0Client } from '@/utils/auth0Client'
import { signOutCognito } from '@/utils/cognitoPoolConfig'

type LogoutOptions = {
  // Where the user should return after logout (for providers like Auth0)
  returnToUrl?: string
  // SPA route to redirect to after logout
  postLogoutRoute?: string
}

type IdentityProvider = {
  name: string
  canHandle: () => Promise<boolean> | boolean
  performLogout: (options: LogoutOptions) => Promise<void>
}

function getDefaultOptions(): Required<LogoutOptions> {
  return {
    returnToUrl: `${window.location.origin}/`,
    postLogoutRoute: '/auth/login',
  }
}

const auth0Provider: IdentityProvider = {
  name: 'auth0',
  canHandle: () => {
    const auth0 = getAuth0Client()
    return !!(auth0 && auth0.isAuthenticated?.value)
  },
  performLogout: async (options: LogoutOptions) => {
    const auth0 = getAuth0Client()
    if (!auth0) return
    const { returnToUrl } = { ...getDefaultOptions(), ...options }
    await auth0.logout({ logoutParams: { returnTo: returnToUrl } })
  },
}

const cognitoProvider: IdentityProvider = {
  name: 'cognito',
  canHandle: () => true,
  performLogout: async (options: LogoutOptions) => {
    const { postLogoutRoute } = { ...getDefaultOptions(), ...options }
    await signOutCognito()
    router.push({ path: postLogoutRoute })
  },
}

const providers: IdentityProvider[] = [auth0Provider, cognitoProvider]

export async function appLogout(options: LogoutOptions = {}): Promise<void> {
  const userStore = useUserStore()
  const societyStore = useSocietyStore()

  userStore.clearUser()
  societyStore.clearSociety()
  useHomeStore().clear()
  useInboxStore().clear()
  useBackgroundTasksStore().clear()

  for (const provider of providers) {
    try {
      const can = await provider.canHandle()
      if (can) {
        await provider.performLogout(options)
        return
      }
    } catch (e) {
      console.error(e);
    }
  }

  const { postLogoutRoute } = { ...getDefaultOptions(), ...options }
  router.push({ path: postLogoutRoute })
}


